// types
import type { OpenRepairOrder, StateTree, VehicleRepairOrdersState } from '@makemydeal/dr-dash-types';

// selectors
import * as vehicleSelectors from './vehicleSelectors';

const byMostRecentlyUpdated = (left: OpenRepairOrder, right: OpenRepairOrder): number => {
    const leftDate = left.lastUpdatedDate ?? '';
    const rightDate = right.lastUpdatedDate ?? '';

    if (!leftDate && !rightDate) return 0;
    if (!leftDate) return 1;
    if (!rightDate) return -1;

    return rightDate.localeCompare(leftDate);
};

const getVehicleState = (state: StateTree, vin?: string): VehicleRepairOrdersState | undefined =>
    vin ? state.openRepairOrders?.byVin?.[vin.toUpperCase()] : undefined;

const sortedRepairOrders = (vehicleState?: VehicleRepairOrdersState): OpenRepairOrder[] =>
    [...(vehicleState?.repairOrders ?? [])].sort(byMostRecentlyUpdated);

// Responses are held per vin, so no vin on the deal — quote mode without a car, or a built vehicle —
// means there is nothing to look up, and nothing survives from whatever vehicle was on the deal before.
const getRepairOrdersForVehicle = (state: StateTree): VehicleRepairOrdersState | undefined =>
    getVehicleState(state, vehicleSelectors.getVehicleVin(state));

export const getRepairOrders = (state: StateTree): OpenRepairOrder[] => sortedRepairOrders(getRepairOrdersForVehicle(state));

export const getRepairOrderAlertCount = (state: StateTree): number => getRepairOrders(state).length;

export const getRepairOrdersLoading = (state: StateTree): boolean => Boolean(getRepairOrdersForVehicle(state)?.isLoading);

/**
 * Repair orders for a named car rather than for the scope doing the asking. The repair orders drawer
 * needs this: it renders once per page, outside any scenario's scope, so it cannot read the vin of the
 * scenario whose alert opened it.
 */
export const getRepairOrdersByVin = (state: StateTree, vin?: string): OpenRepairOrder[] =>
    sortedRepairOrders(getVehicleState(state, vin));

// True once a request for this vin is in flight or already answered. Scenarios sharing a vehicle
// share the entry, which is what keeps a multi-scenario deal to one request per car.
export const hasRequestedRepairOrders = (state: StateTree, vin: string): boolean => Boolean(getVehicleState(state, vin));
